import { useState } from 'react';
import { Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TableSortLabel, Chip, IconButton, Tooltip, Box } from '@mui/material';
import { CheckCircle, Hourglass, Circle, Bell, Eye, History } from 'lucide-react';
// TODO: need api-client equivalent for FundSubmissionStatus type
interface FundSubmissionStatus {
  company_id: string;
  company_name: string;
  submission_id: string | null;
  status: 'draft' | 'submitted' | 'processing' | 'completed' | 'needs_review' | null;
  submitted_at: string | null;
  metrics_count: number;
  metrics?: any[];
  portfolio_companies?: { id: string; name: string };
  period?: string;
  created_at?: string;
  last_email_sent_at?: string | null;
  email_status?: string | null;
  reminder_count?: number;
}

interface CompanyStatusTableProps {
  submissions: FundSubmissionStatus[];
  onViewSubmission?: (submissionId: string) => void;
  onViewTimeline?: (submissionId: string) => void;
  onSendReminder?: (companyId: string) => void;
}

type SortField = 'company_name' | 'fund_name' | 'status' | 'submitted_at';
type SortOrder = 'asc' | 'desc';

export function CompanyStatusTable({ submissions, onViewSubmission, onViewTimeline, onSendReminder }: CompanyStatusTableProps) {
  const [sortField, setSortField] = useState<SortField>('status');
  const [sortOrder, setSortOrder] = useState<SortOrder>('asc');

  // Only show companies with submission records
  const activeSubmissions = submissions.filter((s) => s.status !== null);

  // Check if submissions include fund_name (multi-fund view)
  const hasFundColumn = activeSubmissions.length > 0 && activeSubmissions[0] && 'fund_name' in activeSubmissions[0];

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortOrder('asc');
    }
  };

  const getStatusIcon = (status: string | null) => {
    switch (status) {
      case 'completed':
        return <CheckCircle size={16} strokeWidth={1} color="#2e7d32" />;
      case 'processing':
        return <Hourglass size={16} strokeWidth={1} color="#ed6c02" />;
      default:
        return <Circle size={16} strokeWidth={1} color="#9e9e9e" />;
    }
  };

  const getStatusLabel = (status: string | null) => {
    if (!status || status === 'draft') return 'Not Started';
    return status.charAt(0).toUpperCase() + status.slice(1);
  };

  const getStatusColor = (status: string | null) => {
    switch (status) {
      case 'completed':
        return 'success';
      case 'processing':
        return 'warning';
      case 'submitted':
        return 'info';
      case 'needs_review':
        return 'error';
      default:
        return 'default';
    }
  };

  const sortedSubmissions = [...activeSubmissions].sort((a, b) => {
    let comparison = 0;

    switch (sortField) {
      case 'company_name':
        comparison = a.company_name.localeCompare(b.company_name);
        break;
      case 'fund_name': {
        const aFund = (a as any).fund_name || '';
        const bFund = (b as any).fund_name || '';
        comparison = aFund.localeCompare(bFund);
        break;
      }
      case 'status': {
        const statusOrder = { draft: 0, null: 0, processing: 1, submitted: 2, completed: 3 };
        const aOrder = statusOrder[a.status as keyof typeof statusOrder] ?? 0;
        const bOrder = statusOrder[b.status as keyof typeof statusOrder] ?? 0;
        comparison = aOrder - bOrder;
        break;
      }
      case 'submitted_at': {
        const aDate = a.submitted_at ? new Date(a.submitted_at).getTime() : 0;
        const bDate = b.submitted_at ? new Date(b.submitted_at).getTime() : 0;
        comparison = aDate - bDate;
        break;
      }
    }

    return sortOrder === 'asc' ? comparison : -comparison;
  });

  const formatDate = (dateString: string | null) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
    });
  };

  return (
    <TableContainer component={Paper}>
      <Table>
        <TableHead>
          <TableRow>
            {hasFundColumn && (
              <TableCell>
                <TableSortLabel active={sortField === 'fund_name'} direction={sortField === 'fund_name' ? sortOrder : 'asc'} onClick={() => handleSort('fund_name')}>
                  Fund
                </TableSortLabel>
              </TableCell>
            )}
            <TableCell>
              <TableSortLabel active={sortField === 'company_name'} direction={sortField === 'company_name' ? sortOrder : 'asc'} onClick={() => handleSort('company_name')}>
                Company
              </TableSortLabel>
            </TableCell>
            <TableCell>
              <TableSortLabel active={sortField === 'status'} direction={sortField === 'status' ? sortOrder : 'asc'} onClick={() => handleSort('status')}>
                Status
              </TableSortLabel>
            </TableCell>
            <TableCell>
              <TableSortLabel active={sortField === 'submitted_at'} direction={sortField === 'submitted_at' ? sortOrder : 'asc'} onClick={() => handleSort('submitted_at')}>
                Submitted
              </TableSortLabel>
            </TableCell>
            <TableCell align="right">Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {sortedSubmissions.map((submission) => (
            <TableRow
              key={submission.company_id}
              hover
              onClick={() => submission.submission_id && onViewSubmission?.(submission.submission_id)}
              sx={{ cursor: submission.submission_id ? 'pointer' : 'default' }}
            >
              {hasFundColumn && <TableCell>{(submission as any).fund_name}</TableCell>}
              <TableCell>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  {getStatusIcon(submission.status)}
                  {submission.company_name}
                </Box>
              </TableCell>
              <TableCell>
                <Chip label={getStatusLabel(submission.status)} color={getStatusColor(submission.status) as any} size="small" />
              </TableCell>
              <TableCell>{formatDate(submission.submitted_at)}</TableCell>
              <TableCell align="right">
                <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'flex-end' }}>
                  {submission.submission_id && onViewSubmission && (
                    <Tooltip title="View Submission">
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          onViewSubmission(submission.submission_id!);
                        }}
                      >
                        <Eye size={16} strokeWidth={1} />
                      </IconButton>
                    </Tooltip>
                  )}
                  {submission.submission_id && onViewTimeline && (
                    <Tooltip title="Email Timeline">
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          onViewTimeline(submission.submission_id!);
                        }}
                      >
                        <History size={16} strokeWidth={1} />
                      </IconButton>
                    </Tooltip>
                  )}
                  {(!submission.status || submission.status === 'draft') && onSendReminder && (
                    <Tooltip title="Send Reminder">
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          onSendReminder(submission.company_id);
                        }}
                      >
                        <Bell size={16} strokeWidth={1} />
                      </IconButton>
                    </Tooltip>
                  )}
                </Box>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}
